import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { BaseButton, BaseInput } from "@wantable/Shared/Components";
import { useAuth } from "@wantable/Shared/Hooks";
import { ErrorIcon, SuccessIcon } from "@wantable/Shared/Icons";

import CloseButton from "../Shared/CloseButton";
import { validationSchema } from "./validator";
import { addEnterButtonListener } from "../../helpers/addEnterButtonListener";
import { ModalType }  from "../types";

interface IForgotPasswordFormProps {
  onClose: () => void;
  onSwitchModal: (modal: ModalType) => void;
}

type ForgotPasswordFormStatus = "error" | "success" | null;

const ForgotPasswordForm = ({ onClose, onSwitchModal }: IForgotPasswordFormProps) => {
  const [requestingReset, setRequestingReset] = useState(false);
  const [forgotPasswordFormStatus, setForgotPasswordFormStatus] =
    useState<ForgotPasswordFormStatus>(null);
  const [formMessage, setFormMessage] = useState("");
  const { initiatePasswordReset } = useAuth();
  const { values, setFieldValue, handleSubmit, errors, touched } = useFormik({
    initialValues: {
      email: ""
    },
    validateOnBlur: true,
    validationSchema,
    onSubmit: async ({ email }) => {
      try {
        setRequestingReset(true);
        await initiatePasswordReset(email);
        setForgotPasswordFormStatus("success");
      } catch (error) {
        // @ts-ignore
        setFormMessage(error.message);
        setForgotPasswordFormStatus("error");
      } finally {
        setRequestingReset(false);
      }
    },
  });

  useEffect(() => {
    return addEnterButtonListener(handleSubmit);
  }, []);

  return (
    <div>
      {forgotPasswordFormStatus === "success" && (
        <div
          className="w-full mx-[13px] md:w-[440px] bg-white rounded-10 py-[30px] px-[15px] md:px-[56px] flex flex-col items-center cursor-auto relative">
          <CloseButton onClick={onClose} />
          <h2 className="text-mobile-heading-3 md:text-desktop-heading-3 font-black font-primary text-center">
            Password reset email sent
          </h2>
          <p className="mt-[12px] mb-[10px] text-mobile-body-lg font-secondary text-center">
            We've sent a link to reset your password to your email. Follow the directions in the email to reset your password.
            <br />
            If you can't find the email check your spam folder.
          </p>
        </div>
      )}
      {forgotPasswordFormStatus !== "success" && (
        <form
          onClick={(event) => event.stopPropagation()}
          onSubmit={(event) => event.preventDefault()}
          className="w-full mx-[13px] md:w-[440px] bg-white rounded-10 py-[30px] px-[15px] md:px-[56px] flex flex-col items-center cursor-auto relative"
        >
          <CloseButton onClick={onClose} />

          <h2 className="text-mobile-heading-3 md:text-desktop-heading-3 font-black font-primary text-center">
            Forgot Your Password?
          </h2>
          <p className="mt-[12px] mb-[10px] text-mobile-body-lg font-secondary text-center">
            Enter your email address and we will send you a link to reset your password
          </p>
          <div
            className={`w-full flex flex-row items-center justify-center text-mobile-body md:text-desktop-body text-core-red font-medium font-secondary mb-[15px]`}
          >
            <ErrorIcon hidden={forgotPasswordFormStatus !== "error"} className="pr-4" />
            {formMessage}
          </div>

          <fieldset className="w-full flex flex-col items-center gap-[10px]">
            <BaseInput
              type="text"
              placeholder="Enter email"
              label="Email Address"
              value={values.email}
              name="email"
              onChange={(value) => setFieldValue("email", value)}
              onBlur={() => {}}
              error={touched.email ? errors.email : undefined}
            />

            <div className="w-full">
              <BaseButton
                mode="button"
                onClick={handleSubmit}
                fullWidth
                isSubmitButton
                disabled={requestingReset}
                showSpinner={requestingReset}
              >
                Reset Password
              </BaseButton>
            </div>
          </fieldset>

          <div className="w-full flex items-center justify-center mt-30">
            <BaseButton mode="button" variant="plainUnderline" onClick={() => onSwitchModal("login")}>
              Cancel Password Reset
            </BaseButton>
          </div>
        </form>
      )}
    </div>
  );
};

export default ForgotPasswordForm;
